import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, User, Phone, Mail, Building2, AlertCircle, Loader2, UserPlus } from "lucide-react";
import { adminGet } from "./utils";
import { PatientProfileDrawer } from "./PatientProfileDrawer";

export function PatientsPanel({ auth }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ patients: [], group_members: [], guest_bookees: [], count: 0 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const data = await adminGet(auth, `patients${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setResults(data);
    } catch (err) {
      toast.error(err.message || "Search failed");
    } finally {
      setLoading(false);
    }
  }, [auth, q]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  const rows = [
    ...(results.patients || []),
    ...(results.group_members || []),
    ...(results.guest_bookees || []),
  ];

  return (
    <div className="space-y-4" data-testid="patients-panel">
      <div className="bg-white rounded-2xl border border-brand-navy/10 p-4 flex items-center gap-3">
        <Search className="w-5 h-5 text-brand-muted shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone, email, Medicaid ID, insurance, facility, or trip address"
          data-testid="patient-search-input"
          className="flex-1 bg-transparent outline-none text-sm text-brand-deep placeholder:text-brand-muted"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-brand-muted" />}
      </div>

      {rows.length > 0 && (
        <div className="text-xs text-brand-muted px-1 flex flex-wrap gap-3">
          <span data-testid="count-patients">Patients: <b className="text-brand-deep">{(results.patients || []).length}</b></span>
          <span data-testid="count-group-members">Facility members: <b className="text-brand-deep">{(results.group_members || []).length}</b></span>
          <span data-testid="count-guest-bookees">Guest bookees: <b className="text-brand-deep">{(results.guest_bookees || []).length}</b></span>
        </div>
      )}

      {rows.length === 0 && !loading ? (
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-10 text-center text-brand-muted">
          <User className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <div className="text-sm">{q ? "No matches — try a different search." : "Type to search patients, facility members, and guest bookees."}</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {rows.map((p) => {
            const isGuest = p.source === "guest_ride";
            const isGroup = p.source === "group_member";
            const badgeLabel = isGuest ? "Guest bookee" : isGroup ? "Facility member" : "Patient portal";
            const badgeCls = isGuest
              ? "text-amber-900 bg-amber-100 border-amber-300"
              : isGroup
                ? "text-blue-900 bg-blue-100 border-blue-300"
                : "text-emerald-900 bg-emerald-100 border-emerald-300";
            return (
              <button
                key={`${p.source}-${p.id}`}
                onClick={() => setSelected(p.id)}
                data-testid={`patient-row-${p.id}`}
                className={`text-left bg-white rounded-2xl border p-4 hover:border-brand-green transition ${p.action_needed ? "border-red-400 ring-2 ring-red-200" : "border-brand-navy/10"}`}
              >
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-lg text-brand-deep leading-tight truncate">{p.name || "Unnamed"}</div>
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-[0.14em] px-2 py-0.5 rounded-full border ${badgeCls}`}>
                        {isGuest ? <UserPlus className="w-3 h-3" /> : isGroup ? <Building2 className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {badgeLabel}
                      </span>
                      {p.group_name && !isGuest && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">· {p.group_name}</span>
                      )}
                      {isGuest && typeof p.ride_count === "number" && (
                        <span className="text-[10px] uppercase tracking-[0.14em] text-brand-muted">· {p.ride_count} ride{p.ride_count === 1 ? "" : "s"}</span>
                      )}
                    </div>
                  </div>
                  {p.action_needed && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-[0.14em] px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-300 shrink-0">
                      <AlertCircle className="w-3 h-3" /> Action
                    </span>
                  )}
                </div>
                <div className="space-y-0.5 text-xs text-brand-muted">
                  {p.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {p.phone}</div>}
                  {p.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {p.email}</div>}
                  {p.home_address && <div className="truncate">{p.home_address}</div>}
                  {p.medicaid_id && <div className="text-brand-green font-medium">Medicaid: {p.medicaid_id}</div>}
                  {isGuest && p.outstanding_balance_cents > 0 && (
                    <div className="text-red-700 font-semibold">Balance: ${(p.outstanding_balance_cents/100).toFixed(2)}</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <PatientProfileDrawer
          patientId={selected}
          auth={auth}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
